import { useAuthMe } from '@h2hspark/auth-ui';
import { Button } from '@h2hspark/ui';
import { Sparkles } from 'lucide-react';
import * as React from 'react';
import { Link } from 'react-router-dom';

import { useMyRegistration } from '../event-registrations/api';
import { useMemberAppAdmission } from '../members/api';

import { checkoutPath, joinThenCheckoutPath, reserveActionCopy } from './constants';
import type { ReserveBarTicketType } from './constants';

/**
 * **The pinned reserve action** (`MEM-020` AC-1, AC-6).
 *
 * ## Transcribed from `Member App.dc.html`, the event screen
 *
 * A bar pinned to the foot of the event detail page: a gradient pill on `--spark-gradient` with a
 * `sparkles` glyph, reading **"Reserve my spot · $25"**, over a scrim that fades the content
 * behind it. `MEM-TKT-001` is the requirement — *"the reserve action is presented in a pinned
 * action bar at the foot of the event detail page"* — and it is the one thing the prototype's
 * event screen drew that this build did not have, named in `notYetAvailable` until now.
 *
 * ## The label is one function, for two acceptance criteria
 *
 * AC-1 is `"Reserve my spot · $25"` and AC-6 is `"Reserve my spot · Free"`, and they are one
 * control differing by one word — the case where two constants in two files drift and nobody
 * notices. `reserveActionLabel` is the one place either is spelled, and the price it interpolates
 * is the **server's** rendering (`PublicTicketType.price`, produced by `formatCents`), never a
 * number this app formats.
 *
 * ## And a third case, for an event that sells more than one (`MEM-048` AC-9)
 *
 * This bar was handed `ticketTypes[0]`, so a three-tier event running $20–$50 read **"$25"** — a
 * price that was true of one row and read as true of the event, and a member who tapped it
 * reached a checkout where the tier they wanted cost more than the number that brought them
 * there. Sumit ruled on 2026-08-29 that a multi-tier event reads **"from $20"**, at the cheapest
 * type's own price, and that a single-tier event keeps AC-1's wording exactly.
 *
 * So the decision moved off this component and into `reserveActionCopy`, which is handed the
 * whole list and answers with both strings. Two consequences worth stating: the single-tier and
 * free labels still come out of `reserveActionLabel` itself rather than out of a second spelling
 * of them, and the price in every case is still a row's own served `price` string — this file
 * compares no amounts and formats none.
 *
 * ## Quantity is stated, not offered
 *
 * `MEM-TKT-001`'s correction: *"the organizer surface renders 'Max per person' as a select with
 * the value '1 ticket', implying configurability. Quantity is fixed at one in MVP and the control
 * is a static statement, not a choice."* So the line above the button names the ticket type and
 * says "One ticket per person." — no stepper, no disabled quantity field, nothing that offers a
 * decision the platform will not honour.
 *
 * ## When it is not drawn, and why each case is somebody else's
 *
 *   - **Already registered.** `MEM-017` AC-1's confirmed panel is the answer for that member, and
 *     it is above the location card where the story puts it. Offering "Reserve my spot" to
 *     somebody who has one is the bug the panel exists to prevent.
 *   - **Not open for registration.** The caller does not render this at all — see
 *     `event-detail-page.tsx`, where the same `detail.openForRegistration` that gates
 *     `RegistrationPanel` gates this. `EVT-013` AC-5 requires the register action to *go* on a
 *     cancelled event, and `openForRegistration` is the server's answer rather than an inference
 *     from the lifecycle state.
 *
 * It renders `null` for a member who already holds a place rather than an explanation, because
 * the explanation is already on the screen a few centimetres up.
 *
 * ## A signed-out visitor gets the bar, and `MEM-021` AC-6 is why
 *
 * This used to render `null` when signed out, on the argument that `MEM-019` AC-2 keeps
 * registration off a signed-out visitor's page and `RegistrationPanel` already says so in the page
 * body. `MEM-021` AC-6 supersedes that: *"a signed-out visitor selecting reserve is taken through
 * account creation and profile completion before payment, then returned to payment for the same
 * event"* — so there has to be a reserve action for them to select.
 *
 * The two criteria are not in conflict once the destination is right. The bar does **not**
 * register anybody and does **not** open checkout, so no seat is held and nothing is available
 * signed out that `MEM-019` says is not; it links to `/app/join` carrying this event's checkout as
 * `returnTo`. `RegistrationPanel`'s sentence in the page body stays as it is — it explains, and
 * this acts, which is the division the panel's own header already draws for a registered member.
 *
 * ## And so does a signed-in account with no member profile (`USR-014` AC-2)
 *
 * An organizer or an administrator signing in to the Member App is **admitted** — nothing here
 * refuses them, and AC-1 says nothing may. What they get is the same destination the signed-out
 * visitor gets, for the same reason: the next thing that has to happen is `USR-003`'s wizard, and
 * `joinThenCheckoutPath` carries this event's checkout so they come back to the seat they were
 * reaching for. One control, one label, now three cases and still one destination rule — *go to
 * whatever is outstanding before the money*.
 *
 * It is deliberately **not** a disabled button or a different label. The account has done nothing
 * wrong and the seat is available to them; the only thing between them and it is four screens of
 * questions, and a control that says "Reserve my spot" and takes them to the first of those is a
 * truer description of what pressing it does than a greyed-out one would be. The explanation is on
 * the screen it arrives at, where somebody is in a position to act on it (AC-5).
 *
 * The API refuses the same case at `POST /events/:eventId/checkout`, so this is the courtesy and
 * not the control — see `EventOrdersService.assertMemberProfileFinished`.
 */
export function ReserveActionBar({
  eventId,
  ticketTypes,
}: {
  readonly eventId: string;
  /**
   * **Every** ticket type this event sells, in the organizer's own order (`EVT-017` AC-4), and
   * empty on a free event — which carries none.
   *
   * It used to be the first type's name and the first type's price, two scalars, and that was the
   * bug: a bar handed `ticketTypes[0]` cannot tell a one-tier event from the cheapest row of five.
   * Handing it the list is what makes `MEM-048` AC-9's two cases decidable at all.
   *
   * The list carries no seat counts and there is no field on it to draw one from — capacity is per
   * event (`EVT-020`) and a per-tier count is the allocation system `EVT-019` AC-3 forbids.
   */
  readonly ticketTypes: ReadonlyArray<ReserveBarTicketType>;
}): React.ReactElement | null {
  const { user, loading } = useAuthMe();
  const signedIn = user !== null;
  const registration = useMyRegistration(eventId, { enabled: signedIn });
  const admission = useMemberAppAdmission(signedIn);
  const copy = reserveActionCopy(ticketTypes);

  if (loading && user === null) return null;
  if (signedIn && registration.isPending) return null;
  if ((registration.data?.registration ?? null) !== null) return null;
  // Nothing is drawn until the destination is known. A bar rendered mid-flight would point at
  // checkout and then quietly change under somebody's thumb, which is worse than a moment of
  // nothing — and this is already how the registration read above is treated.
  if (signedIn && admission.isPending) return null;
  // `true` only for `member_profile_incomplete`; the server decides which states ask, and a failed
  // read leaves this `undefined`, which sends a member to the checkout they were always going to.
  const memberProfileOutstanding = admission.data?.memberProfileRequired === true;

  return (
    <div
      /*
       * **Pinned on a phone and a tablet, in the sidebar from `lg`** (`MEM-056` AC-4,
       * `Web App.dc.html` §2.2).
       *
       * Below `md` it is the phone design's bar: pinned above the tab bar (`z-30`, `bottom-0`)
       * rather than over it, over the prototype's scrim — content fades into the bar rather than
       * being cut by a hard edge, so a member can see there is more page underneath. From `md`
       * there is no tab bar, so it drops to `bottom-0`; the event page is still one column there
       * (`MemberEventView` says why the columns wait for `lg`).
       *
       * From `lg` the same element is `static`, in flow in the sticky sidebar, between the price
       * and Who's coming, as the desktop prototype draws its *Reserve my spot* button. One control
       * that moves rather than two that each hide at a width: two reserve actions on one page can
       * disagree about the price (see `RegistrationPanel`'s last branch), and a width that swapped
       * one for the other would be a place the phone and the desktop offer different things.
       */
      className="fixed inset-x-0 bottom-[62px] z-40 w-full bg-gradient-to-t from-background from-70% to-transparent px-5 pb-3 pt-6 md:bottom-0 md:pb-5 lg:static lg:z-auto lg:bg-none lg:p-0"
    >
      {/* The bar and its scrim span the screen, as the phone layout below `md` now does; the
          control keeps the phone canvas's width inside it, so it is not a 700px button on a small
          tablet. It was a 390px bar floating in the middle of the page (reviewing #526). In the
          sidebar it takes the sidebar's width, as the prototype's full-width button does. */}
      <div className="mx-auto max-w-app-canvas lg:max-w-none">
        {/* **A pill in `font-brand` 700, at both widths.** §2.2's sidebar draws this as the
            design's spark button — `--radius-pill`, `font-brand` 700 at `--text-md` over
            `--shadow-accent` — and `Member App.dc.html`'s pinned bar draws the same pill. It was
            inheriting `Button`'s `size="lg"` defaults instead: a 12px corner at weight 500, which
            made the one control the page exists for the only non-pill on it (parity pass,
            2026-09-12). */}
        <Button
          className="w-full rounded-pill bg-spark font-brand font-bold text-white shadow-accent hover:bg-spark lg:h-auto lg:py-[15px] lg:text-md"
          size="lg"
          asChild
        >
          {/* `MEM-021` AC-6: a visitor goes to account creation carrying this event's checkout,
            and a member goes straight to it. One control, one label, two destinations — because
            what the member is choosing is the same thing in both cases. */}
          <Link
            to={
              signedIn && !memberProfileOutstanding
                ? checkoutPath(eventId)
                : joinThenCheckoutPath(eventId)
            }
          >
            <Sparkles className="mr-2 h-[18px] w-[18px]" aria-hidden />
            {copy.label}
          </Link>
        </Button>
        <p className="mt-2 text-center text-xs text-muted-foreground">{copy.line}</p>
      </div>
    </div>
  );
}
